import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

export function InstallPrompt({
  appDisplayName,
}: {
  readonly appDisplayName?: string;
} = {}) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    if (isIos()) {
      setShowIosHint(true);
      return;
    }

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  if (dismissed || isStandalone()) return null;
  if (!deferredPrompt && !showIosHint) return null;

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }

  return (
    <aside className="install-prompt" aria-label="Install app">
      <div className="install-prompt-copy">
        <p className="value-card-eyebrow">Install</p>
        <p className="install-prompt-headline">
          Add {appDisplayName?.trim() || "your workspace"} to your home screen
        </p>
        {showIosHint ? (
          <p>
            On iPhone or iPad: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
          </p>
        ) : (
          <p>Install for quicker access to your review room.</p>
        )}
      </div>
      <div className="action-row install-prompt-actions">
        {deferredPrompt ? (
          <button type="button" className="btn-primary" onClick={() => void handleInstall()}>
            Install app
          </button>
        ) : null}
        <button type="button" className="btn-secondary" onClick={() => setDismissed(true)}>
          Not now
        </button>
      </div>
    </aside>
  );
}
